import React, { useEffect } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';

import StorefrontLayout from './components/StorefrontLayout';
import { RequireAuth, RequireAdmin } from './components/ProtectedRoute';

// User pages
import Home from './pages/user/Home';
import Shop from './pages/user/Shop';
import ProductDetail from './pages/user/ProductDetail';
import Cart from './pages/user/Cart';
import Wishlist from './pages/user/Wishlist';
import Checkout from './pages/user/Checkout';
import Login from './pages/user/Login';
import Register from './pages/user/Register';
import ForgotPassword from './pages/user/ForgotPassword';
import ResetPassword from './pages/user/ResetPassword';
import Orders from './pages/user/Orders';
import OrderDetail from './pages/user/OrderDetail';
import Profile from './pages/user/Profile';
import Notifications from './pages/user/Notifications';
import AboutUs from './pages/user/AboutUs';
import Authenticity from './pages/user/Authenticity';
import Blog from './pages/user/Blog';
import BlogDetail from './pages/user/BlogDetail';
import ContactUs from './pages/user/ContactUs';
import PrivacyPolicy from './pages/user/PrivacyPolicy';
import Terms from './pages/user/Terms';
import ReturnsPolicy from './pages/user/ReturnsPolicy';
import CareGuide from './pages/user/CareGuide';
import PaymentReturn from './pages/user/PaymentReturn';
import Invoice from './pages/user/Invoice';

// Admin pages
import AdminLayout from './components/admin/AdminLayout';
import AdminLogin from './pages/admin/AdminLogin';
import Dashboard from './pages/admin/Dashboard';
import AdminProducts from './pages/admin/AdminProducts';
import AdminProductForm from './pages/admin/AdminProductForm';
import AdminCategories from './pages/admin/AdminCategories';
import AdminVariants from './pages/admin/AdminVariants';
import AdminBlogs from './pages/admin/AdminBlogs';
import AdminBlogForm from './pages/admin/AdminBlogForm';
import AdminOrders from './pages/admin/AdminOrders';
import AdminOrderDetail from './pages/admin/AdminOrderDetail';
import AdminTransactions from './pages/admin/AdminTransactions';
import AdminPromoCodes from './pages/admin/AdminPromoCodes';
import AdminCustomers from './pages/admin/AdminCustomers';
import AdminLeads from './pages/admin/AdminLeads';
import AdminRoles from './pages/admin/AdminRoles';
import AdminUsers from './pages/admin/AdminUsers';
import AdminShipping from './pages/admin/AdminShipping';
import AdminSettings from './pages/admin/AdminSettings';
import AdminNotifications from './pages/admin/AdminNotifications';
import AdminSystem from './pages/admin/AdminSystem';
import AdminProfile from './pages/admin/AdminProfile';

// Automatic Scroll To Top Component on Route / Search Parameter Change
function ScrollToTop() {
  const { pathname, search } = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [pathname, search]);

  return null;
}

export default function App() {
  return (
    <>
      <ScrollToTop />
      <Routes>
        {/* Storefront */}
        <Route element={<StorefrontLayout />}>
          <Route path="/" element={<Home />} />
          <Route path="/shop" element={<Shop />} />
          <Route path="/about" element={<AboutUs />} />
          <Route path="/authenticity" element={<Authenticity />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogDetail />} />
          <Route path="/contact" element={<ContactUs />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-and-conditions" element={<Terms />} />
          <Route path="/returns-and-refunds" element={<ReturnsPolicy />} />
          <Route path="/care-guide" element={<CareGuide />} />
          <Route path="/product/:slug" element={<ProductDetail />} />
          <Route path="/cart" element={<Cart />} />
          <Route path="/wishlist" element={<Wishlist />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password/:token" element={<ResetPassword />} />
          <Route path="/checkout" element={<RequireAuth><Checkout /></RequireAuth>} />
          <Route path="/orders" element={<RequireAuth><Orders /></RequireAuth>} />
          <Route path="/orders/:id" element={<RequireAuth><OrderDetail /></RequireAuth>} />
          <Route path="/orders/:id/invoice" element={<RequireAuth><Invoice /></RequireAuth>} />
          <Route path="/profile" element={<RequireAuth><Profile /></RequireAuth>} />
          <Route path="/notifications" element={<RequireAuth><Notifications /></RequireAuth>} />
          <Route path="/payment/stripe/return" element={<RequireAuth><PaymentReturn provider="stripe" /></RequireAuth>} />
          <Route path="/payment/paypal/return" element={<RequireAuth><PaymentReturn provider="paypal" /></RequireAuth>} />
          <Route path="/payment/cancel" element={<RequireAuth><PaymentReturn cancelled /></RequireAuth>} />
        </Route>

        {/* Admin Panel */}
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route
          path="/admin"
          element={
            <RequireAdmin>
              <AdminLayout />
            </RequireAdmin>
          }
        >
          <Route index element={<RequireAdmin moduleKey="dashboard" action="view"><Dashboard /></RequireAdmin>} />
          <Route path="products" element={<RequireAdmin moduleKey="products" action="view"><AdminProducts /></RequireAdmin>} />
          <Route path="products/new" element={<RequireAdmin moduleKey="products" action="create"><AdminProductForm /></RequireAdmin>} />
          <Route path="products/:id" element={<RequireAdmin moduleKey="products" action="edit"><AdminProductForm /></RequireAdmin>} />
          <Route path="categories" element={<RequireAdmin moduleKey="categories" action="view"><AdminCategories /></RequireAdmin>} />
          <Route path="variants" element={<RequireAdmin moduleKey="products" action="manage_variants"><AdminVariants /></RequireAdmin>} />
          <Route path="blogs" element={<RequireAdmin moduleKey="blogs" action="view"><AdminBlogs /></RequireAdmin>} />
          <Route path="blogs/new" element={<RequireAdmin moduleKey="blogs" action="create"><AdminBlogForm /></RequireAdmin>} />
          <Route path="blogs/:id" element={<RequireAdmin moduleKey="blogs" action="edit"><AdminBlogForm /></RequireAdmin>} />
          <Route path="orders" element={<RequireAdmin moduleKey="orders" action="view"><AdminOrders /></RequireAdmin>} />
          <Route path="orders/:id" element={<RequireAdmin moduleKey="orders" action="view"><AdminOrderDetail /></RequireAdmin>} />
          <Route path="transactions" element={<RequireAdmin moduleKey="transactions" action="view"><AdminTransactions /></RequireAdmin>} />
          <Route path="promo-codes" element={<RequireAdmin moduleKey="promo_codes" action="view"><AdminPromoCodes /></RequireAdmin>} />
          <Route path="customers" element={<RequireAdmin moduleKey="customers" action="view"><AdminCustomers /></RequireAdmin>} />
          <Route path="leads" element={<RequireAdmin moduleKey="leads" action="view"><AdminLeads /></RequireAdmin>} />
          <Route path="roles" element={<RequireAdmin moduleKey="users_roles" action="view"><AdminRoles /></RequireAdmin>} />
          <Route path="admins" element={<RequireAdmin moduleKey="users_roles" action="view"><AdminUsers /></RequireAdmin>} />
          <Route path="shipping" element={<RequireAdmin moduleKey="shipping" action="view"><AdminShipping /></RequireAdmin>} />
          <Route path="notifications" element={<RequireAdmin moduleKey="notifications" action="view"><AdminNotifications /></RequireAdmin>} />
          <Route path="settings" element={<RequireAdmin anyPermissions={[["settings_general", "view"], ["settings_smtp", "view"], ["settings_payment", "view"]]}><AdminSettings /></RequireAdmin>} />
          <Route path="system" element={<RequireAdmin superAdminOnly><AdminSystem /></RequireAdmin>} />
          <Route path="profile" element={<AdminProfile />} />
        </Route>

        <Route path="*" element={<div className="p-20 text-center font-heading text-2xl text-primaryDark">Page Not Found</div>} />
      </Routes>
    </>
  );
}
